/**
 * R14.4 — Host Escalations standalone page.
 *
 * Renders the HostApprovalTray outside the LiveKit room context so the
 * host (or co-host) can review and decide on bot escalations from
 * their dashboard, a popout, or before joining the room. Hydrates
 * from the same `/api/rooms/{room_id}/host-approval/pending` endpoint
 * the in-room tray now uses (R14.4 hydration fix).
 *
 * Route: `/dashboard/rooms/:roomId/escalations`
 *
 * Testids:
 *   - host-escalations-root
 *   - host-escalations-header
 *   - host-escalations-empty
 *   - host-escalations-error
 *   - reuses host-approval-tray / host-approval-card-{id} / -allow / -deny / -snooze
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../lib/auth-context";
import { Loader2, Shield, ArrowLeft } from "lucide-react";
import HostApprovalTray from "../components/HostApprovalTray";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function HostEscalations() {
  const { roomId } = useParams();
  const { user } = useAuth();
  const [cards, setCards] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [room, setRoom] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/rooms/${roomId}/host-approval/pending`, {
        credentials: "include",
      });
      if (r.status === 403) {
        setError("Only the room host or cohosts can view escalations.");
        setLoading(false);
        return;
      }
      if (!r.ok) {
        setError(`Backend returned ${r.status}.`);
        setLoading(false);
        return;
      }
      const data = await r.json();
      setCards(data.cards || []);
      setError("");
      setLoading(false);
    } catch (e) {
      setError(e?.message || "Network error");
      setLoading(false);
    }
  }, [roomId]);

  useEffect(() => {
    fetch(`${API}/api/rooms/${roomId}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then(setRoom)
      .catch(() => {});
    refresh();
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, [roomId, refresh]);

  const handleDecision = useCallback(async ({ card_id, decision }) => {
    try {
      const r = await fetch(`${API}/api/rooms/${roomId}/host-approval/${card_id}/${decision}`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) throw new Error(`Decision failed (${r.status})`);
      setCards((p) => p.filter((c) => c.card_id !== card_id));
      toast.success(`Card ${decision}d`);
    } catch (e) {
      toast.error(e?.message || "Couldn't apply decision");
    }
  }, [roomId]);

  if (user === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#05050a]">
        <Loader2 className="w-6 h-6 animate-spin text-[#00F0FF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05050a] text-white p-6" data-testid="host-escalations-root">
      <header className="max-w-3xl mx-auto mb-5 flex items-center gap-3" data-testid="host-escalations-header">
        <Link to="/" className="p-1.5 rounded hover:bg-white/5" data-testid="host-escalations-back">
          <ArrowLeft className="w-4 h-4 text-white/55" />
        </Link>
        <Shield className="w-5 h-5 text-amber-300" />
        <div>
          <h1 className="font-display text-xl font-bold leading-tight">Bot escalations</h1>
          <p className="text-[11px] text-white/40 font-mono">
            room {roomId}{room?.name ? ` · ${room.name}` : ""}
          </p>
        </div>
        <span className="ml-auto text-[10px] uppercase tracking-[0.18em] text-amber-300/85 px-2 py-1 rounded bg-amber-500/10 border border-amber-500/30">
          {cards.length} pending
        </span>
      </header>

      {loading && (
        <div className="max-w-3xl mx-auto text-center text-white/55 text-sm py-12">
          <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2" /> Loading…
        </div>
      )}

      {!loading && error && (
        <div
          className="max-w-3xl mx-auto bg-red-500/10 border border-red-500/35 rounded-lg p-4 text-sm text-red-200"
          data-testid="host-escalations-error"
        >
          {error}
        </div>
      )}

      {!loading && !error && cards.length === 0 && (
        <div
          className="max-w-3xl mx-auto text-center text-white/40 text-sm py-12 border border-dashed border-white/10 rounded-lg"
          data-testid="host-escalations-empty"
        >
          No pending escalations.
        </div>
      )}

      {!loading && !error && cards.length > 0 && (
        <div className="max-w-3xl mx-auto">
          {/* Reuses the in-room tray styling. isStaff=true so it renders the cards. */}
          <HostApprovalTrayInline cards={cards} onDecision={handleDecision} />
        </div>
      )}
    </div>
  );
}

// Inline wrapper — renders the cards in normal page flow instead of as a
// fixed top-right tray (HostApprovalTray uses fixed positioning which is
// unwanted on a full-page route).
function HostApprovalTrayInline({ cards, onDecision }) {
  return (
    <div className="flex flex-col gap-3" data-testid="host-approval-tray">
      {cards.map((c) => (
        <div
          key={c.card_id}
          className="rounded-lg border border-amber-400/30 bg-[#1a120c]/95 p-4"
          data-testid={`host-approval-card-${c.card_id}`}
        >
          <div className="flex items-start gap-2 mb-2">
            <Shield className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.16em] text-amber-300/85 font-bold">
                {(c.bot || "bot").replace("_", " ")}
              </div>
              <div className="text-base font-semibold mt-0.5 truncate">
                {c.kind === "removal" ? "Remove" : "Review"} {c.target_display_name || c.target_identity}?
              </div>
            </div>
          </div>
          <p className="text-sm text-white/80 leading-snug mb-3">{c.reason}</p>
          {c.evidence?.cam_state && (
            <div className="text-[11px] text-white/45 mb-3">
              Evidence: cam state <span className="font-mono text-white/65">{c.evidence.cam_state}</span>
              {c.evidence.since && (
                <span> · since {new Date((c.evidence.since || 0) * 1000).toLocaleTimeString()}</span>
              )}
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              onClick={() => onDecision({ card_id: c.card_id, decision: "allow" })}
              className="flex-1 px-3 py-2 rounded text-[11px] font-bold uppercase tracking-[0.14em] bg-rose-500/90 hover:bg-rose-500 text-white"
              data-testid={`host-approval-allow-${c.card_id}`}
            >
              Allow (kick)
            </button>
            <button
              onClick={() => onDecision({ card_id: c.card_id, decision: "deny" })}
              className="flex-1 px-3 py-2 rounded text-[11px] font-bold uppercase tracking-[0.14em] bg-white/10 hover:bg-white/15 text-white/85"
              data-testid={`host-approval-deny-${c.card_id}`}
            >
              Deny
            </button>
            <button
              onClick={() => onDecision({ card_id: c.card_id, decision: "snooze" })}
              className="px-3 py-2 rounded text-[11px] font-bold uppercase tracking-[0.14em] bg-white/5 hover:bg-white/10 text-white/55"
              data-testid={`host-approval-snooze-${c.card_id}`}
              title="Snooze 5 min"
            >
              Snooze
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
